import { BrowserRouter, Route, Routes } from 'react-router-dom';
import { createTheme, localStorageColorSchemeManager, MantineProvider } from '@mantine/core';
import { Notifications } from '@mantine/notifications';
import { PageRoutes } from './routes';
import { AuthProvider } from './context/authContext';
import { lazy, Suspense } from 'react';
import CurrentServicePage from './pages/currentService';
import ChurchQRPage from './pages/churchQR';
import { ChurchAccessBoundary } from './components/ChurchAccessBoundary';
import { AuthenticatedBoundary } from './components/AuthenticatedBoundary';
import { AnalyticsPageTracker } from './components/AnalyticsPageTracker';

export const COLOR_SCHEME_STORAGE_KEY = 'living-wire-color-scheme';

const colorSchemeManager = localStorageColorSchemeManager({
  key: COLOR_SCHEME_STORAGE_KEY,
});

const theme = createTheme({
  primaryColor: 'indigo',
  defaultRadius: 'md',
  fontFamily: 'Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  headings: {
    fontFamily: 'Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    fontWeight: '700',
  },
  components: {
    Button: { defaultProps: { radius: 'md' } },
    Paper: { defaultProps: { radius: 'lg' } },
    TextInput: { defaultProps: { radius: 'md' } },
    PasswordInput: { defaultProps: { radius: 'md' } },
    Textarea: { defaultProps: { radius: 'md' } },
    Select: { defaultProps: { radius: 'md' } },
    MultiSelect: { defaultProps: { radius: 'md' } },
  },
});

const HomePage = lazy(() => import('./pages/home'));
const TranslationsPage = lazy(() => import('./pages/translations'));
const LoginPage = lazy(() => import('./pages/login'));
const DashboardPage = lazy(() => import('./pages/dashboard'));
const RegisterPage = lazy(() => import('./pages/register'));
const PrivacyPage = lazy(() => import('./pages/privacy'));
const ServiceDetailPage = lazy(() => import('./pages/service'));
const NewServicePage = lazy(() => import('./pages/newService'));
const ChurchPage = lazy(() => import('./pages/church'));
const ChurchSettingsPage = lazy(() => import('./pages/churchSettings'));
const DesktopHomePage = lazy(() => import('./pages/desktop-home'));
const PricingPage = lazy(() => import('./pages/pricing'));
const CreateChurchPage = lazy(() => import('./pages/create-church'));
const ChurchPendingReviewPage = lazy(() => import('./pages/churchPendingReview'));
const SubscribePage = lazy(() => import('./pages/subscribe'));
const BillingTestPage = lazy(() => import('./pages/billingTest'));
const BillingSuccessPage = lazy(() => import('./pages/billingSuccess'));
const SolutionPage = lazy(() => import('./pages/solution'));

export const App = () => {
  return (
    <MantineProvider
      theme={theme}
      colorSchemeManager={colorSchemeManager}
      defaultColorScheme="auto"
    >
      <AuthProvider>
        <Notifications />
        <BrowserRouter>
          <AnalyticsPageTracker />
          <Suspense fallback={<div style={{ padding: 24 }}>Loading…</div>}>
            <Routes>
              <Route path={PageRoutes.DESKTOP_HOME} element={<DesktopHomePage />} />
              <Route path={PageRoutes.LOGIN} element={<LoginPage />} />
              <Route path={PageRoutes.REGISTER} element={<RegisterPage />} />
              <Route path={PageRoutes.DASHBOARD} element={<AuthenticatedBoundary><DashboardPage /></AuthenticatedBoundary>} />
              <Route path={PageRoutes.PRIVACY} element={<PrivacyPage />} />
              <Route path={PageRoutes.TRANSLATIONS} element={<TranslationsPage />} />
              <Route path={PageRoutes.CHURCH_PAGE} element={<ChurchAccessBoundary><ChurchPage /></ChurchAccessBoundary>} />
              <Route path={PageRoutes.CHURCH_PENDING_REVIEW} element={<ChurchPendingReviewPage />} />
              <Route path={PageRoutes.CHURCH_SETTINGS} element={<ChurchAccessBoundary><ChurchSettingsPage /></ChurchAccessBoundary>} />
              <Route path={PageRoutes.CREATE_CHURCH} element={<AuthenticatedBoundary><CreateChurchPage /></AuthenticatedBoundary>} />
              <Route path={PageRoutes.SERVICE_DETAIL} element={<ChurchAccessBoundary><ServiceDetailPage /></ChurchAccessBoundary>} />
              <Route path={PageRoutes.SERVICE_DETAIL_TAB} element={<ChurchAccessBoundary><ServiceDetailPage /></ChurchAccessBoundary>} />
              <Route path={PageRoutes.NEW_SERVICE} element={<ChurchAccessBoundary><NewServicePage /></ChurchAccessBoundary>} />
              <Route path={PageRoutes.PRICING} element={<PricingPage />} />
              <Route path={PageRoutes.CURRENT_SERVICE} element={<CurrentServicePage />} />
              <Route path={PageRoutes.CHURCH_QR} element={<ChurchAccessBoundary><ChurchQRPage /></ChurchAccessBoundary>} />
              <Route path={PageRoutes.SUBSCRIBE} element={<AuthenticatedBoundary><SubscribePage /></AuthenticatedBoundary>} />
              <Route path={PageRoutes.BILLING_TEST} element={<AuthenticatedBoundary><BillingTestPage /></AuthenticatedBoundary>} />
              <Route path={PageRoutes.BILLING_SUCCESS} element={<AuthenticatedBoundary><BillingSuccessPage /></AuthenticatedBoundary>} />
              <Route path={PageRoutes.SPANISH_TRANSLATION} element={<SolutionPage kind="spanish" />} />
              <Route path={PageRoutes.MULTILINGUAL_CHURCHES} element={<SolutionPage kind="multilingual" />} />
              <Route path={PageRoutes.CHURCH_CAPTIONS} element={<SolutionPage kind="captions" />} />
              <Route index element={<HomePage />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </AuthProvider>
    </MantineProvider>
  );
};
